import {
	ChangeDetectionStrategy,
	Component,
	effect,
	input,
	InputSignal,
	output,
	OutputEmitterRef,
	Signal,
	signal,
	WritableSignal
} from "@angular/core";
import { ISettingSelectOption } from "../../../settings/models/isetting-select-option";
import { ISettingSelectParams } from "../../../settings/models/isetting-select-params";
import { ISettingToggleParams } from "../../../settings/models/isetting-toggle-params";
import { BarecodeReaderSettings } from "../models/barecode-reader-settings";
import { EAccuracyType } from "../models/eaccuracy-type";
import { LogisticsSettings } from "../models/logistics-settings";

@Component({
	selector: "calao-logistics-settings",
	templateUrl: "./logistics-settings.component.html",
	styleUrls: ["./logistics-settings.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class LogisticsSettingsComponent {
	//#region FIELDS

	protected readonly moAccurancyChanged: OutputEmitterRef<EAccuracyType> = output({ alias: "accurancyChanged" });
	protected readonly moDisableDeliveriesTutorialChanged: OutputEmitterRef<boolean> = output({
		alias: "disableDeliveriesTutorialChanged"
	});

	//#endregion FIELDS

	//#region PROPERTIES

	public readonly settingsSignal: InputSignal<LogisticsSettings> = input(new LogisticsSettings(), {
		alias: "settings"
	});

	private readonly moBarcodeReaderModeParamsSignal: WritableSignal<ISettingSelectParams | undefined> =
		signal(undefined);
	public readonly barcodeReaderModeParamsSignal: Signal<ISettingSelectParams | undefined> =
		this.moBarcodeReaderModeParamsSignal.asReadonly();

	private readonly moDisableDeliveriesTutorialParamsSignal: WritableSignal<ISettingToggleParams | undefined> =
		signal(undefined);
	public readonly disableDeliveriesTutorialParamsSignal: Signal<ISettingToggleParams | undefined> =
		this.moDisableDeliveriesTutorialParamsSignal.asReadonly();

	//#endregion PROPERTIES

	//#region METHODS

	constructor() {
		effect(() => this.updateUi(this.settingsSignal()));
	}

	private updateUi(poSettings: LogisticsSettings): void {
		this.setBarcodeReaderModeParams(poSettings.barcodeReader);
		this.setDisableDeliveriesTutorialParams(poSettings.deliveries.disableTutorial);
	}

	private setBarcodeReaderModeParams(poBarcodeReader: BarecodeReaderSettings): void {
		const laOptions: ISettingSelectOption[] = [
			{ id: EAccuracyType.crosshair, text: "Précis" },
			{ id: EAccuracyType.fullSquare, text: "Large" }
		];

		this.moBarcodeReaderModeParamsSignal.set({
			label: "Précision lecteur",
			values: laOptions,
			defaultValue: laOptions.find(
				(poSelectedOption: ISettingSelectOption) => poSelectedOption.id === poBarcodeReader.accuracy
			)
		});
	}

	private setDisableDeliveriesTutorialParams(pbDisabled: boolean): void {
		this.moDisableDeliveriesTutorialParamsSignal.set({
			label: "Désactiver le tutoriel des livraisons",
			value: pbDisabled
		});
	}

	public raiseAccurancyChanged(poAccurancyOption: ISettingSelectOption): void {
		this.moAccurancyChanged.emit(poAccurancyOption.id as EAccuracyType);
	}

	public raiseDisableDeliveriesTutorial(pbDisabled: boolean): void {
		this.moDisableDeliveriesTutorialChanged.emit(pbDisabled);
	}

	//#endregion METHODS
}
